import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Alert, IconButton, CircularProgress, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  MenuItem, FormControlLabel, Switch, Button, Divider
} from '@mui/material';
import { Home, Add, Edit, Delete, Inventory2, ArrowBack, ListAlt, WarningAmber } from '@mui/icons-material';
import axios from 'axios';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';
import { fmt, sym } from '../utils/currency';
import { useGLAccounts } from '../hooks/useGLAccounts';
import { glAccountOptions } from '../utils/glAccountOptions';
import { useLocations } from '../hooks/useLocations';

const ITEM_TYPES = ['Item', 'Service'];
const UOM_OPTIONS = ['Ea', 'Kg', 'Ltr', 'Hr', 'Day', 'Month', 'Set'];

const emptyForm = {
  itemNo: '', itemName: '', itemType: 'Service', description: '',
  unitOfMeasure: 'Ea', unitPrice: '', glAccount: '', defaultLocationCode: '',
  reorderPoint: '', reorderQty: '', isActive: true
};

const InfoRow = ({ label, value }) => (
  <Box display="flex" gap={1} mb={0.5}>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160, fontWeight: 500 }}>{label}</Typography>
    <Typography variant="body2">{value ?? <em style={{ color: '#bbb' }}>—</em>}</Typography>
  </Box>
);

const ItemList = () => {
  const navigate = useNavigate();
  const [items, setItems]       = useState([]);
  const [stockMap, setStockMap] = useState({});
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(true);
  const [viewItem, setViewItem] = useState(null);
  const [dialogOpen, setDialogOpen]     = useState(false);
  const [editingItemNo, setEditingItemNo] = useState(null);
  const [form, setForm]                   = useState(emptyForm);
  const { attemptClose, discardDialog } = useUnsavedGuard({ open: dialogOpen, value: form, onClose: () => setDialogOpen(false) });
  const { accounts: glAccounts } = useGLAccounts();
  const { locations }            = useLocations();

  // Deactivate confirm dialog
  const [deactivateDlg, setDeactivateDlg] = useState(null);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const fetchItems = async () => {
    try {
      const [itemsRes, stockRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/api/payables/items`, { headers }),
        axios.get(`${process.env.REACT_APP_API_URL}/api/payables/item-stock`, { headers })
      ]);
      setItems(itemsRes.data);
      setStockMap(stockRes.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load items');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, []); // eslint-disable-line

  const handleCreate = () => { setEditingItemNo(null); setForm(emptyForm); setDialogOpen(true); };

  const handleEdit = (i) => {
    setEditingItemNo(i.itemNo);
    setForm({ ...emptyForm, ...i, unitPrice: i.unitPrice ?? '' });
    setDialogOpen(true);
  };

  const confirmAction = async () => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/payables/items/${deactivateDlg}`, { headers });
      setDeactivateDlg(null);
      setError('Item deactivated successfully');
      fetchItems();
    } catch (err) {
      setDeactivateDlg(null);
      setError(err.response?.data?.error || 'Failed to deactivate');
    }
  };

  const handleSave = async () => {
    if (!editingItemNo && !form.itemNo.trim()) { setError('Item No is required'); return; }
    if (!form.itemName.trim()) { setError('Item Name is required'); return; }
    if (!form.itemType) { setError('Item Type is required'); return; }
    if (!form.glAccount) { setError('GL Account is required'); return; }
    try {
      const payload = {
        ...form,
        unitPrice:    parseFloat(form.unitPrice) || 0,
        reorderPoint: parseFloat(form.reorderPoint) || 0,
        reorderQty:   parseFloat(form.reorderQty) || 0,
      };
      if (editingItemNo) {
        await axios.put(`${process.env.REACT_APP_API_URL}/api/payables/items/${editingItemNo}`, payload, { headers });
      } else {
        await axios.post(`${process.env.REACT_APP_API_URL}/api/payables/items`, payload, { headers });
      }
      setDialogOpen(false);
      setError('');
      fetchItems();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save item');
    }
  };

  if (loading) return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', mt: 4, textAlign: 'center' }}>
      <CircularProgress /><Typography sx={{ mt: 2 }}>Loading items...</Typography>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', mt: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center">
              <Inventory2 sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h5" color="primary">Items & Services</Typography>
            </Box>
            <Box>
              <IconButton onClick={() => navigate(-1)} title="Back"
                sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' }, mr: 1 }}>
                <ArrowBack />
              </IconButton>
              <IconButton onClick={() => navigate('/dashboard')} title="Dashboard"
                sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' }, mr: 1 }}>
                <Home />
              </IconButton>
              <IconButton onClick={handleCreate} title="Add Item"
                sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}>
                <Add />
              </IconButton>
            </Box>
          </Box>

          {error && <Alert severity={error.includes('success') ? 'success' : 'error'} sx={{ mb: 2 }}>{error}</Alert>}

          {items.length === 0 ? <Typography>No items found.</Typography> : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>No</strong></TableCell>
                  <TableCell><strong>Name</strong></TableCell>
                  <TableCell><strong>Type</strong></TableCell>
                  <TableCell><strong>UoM</strong></TableCell>
                  <TableCell align="right"><strong>Unit Price</strong></TableCell>
                  <TableCell align="right"><strong>Stock Qty</strong></TableCell>
                  <TableCell><strong>GL Account</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map(i => (
                  <TableRow key={i.itemNo} hover sx={{ cursor: 'pointer' }}
                    onClick={() => setViewItem(i)}>
                    <TableCell><strong>{i.itemNo}</strong></TableCell>
                    <TableCell>{i.itemName}</TableCell>
                    <TableCell>{i.itemType}</TableCell>
                    <TableCell>{i.unitOfMeasure}</TableCell>
                    <TableCell align="right">{sym()} {fmt(i.unitPrice || 0)}</TableCell>
                    <TableCell align="right" onClick={e => e.stopPropagation()}>
                      {i.itemType === 'Item' ? (
                        <Chip
                          label={(stockMap[i.itemNo]?.qty || 0).toFixed(2)}
                          color={(stockMap[i.itemNo]?.qty || 0) > 0 ? 'success' : 'default'}
                          size="small" variant="outlined"
                          onClick={() => navigate(`/payables/items/${i.itemNo}/ledger`)}
                          sx={{ cursor: 'pointer', fontWeight: 600 }}
                        />
                      ) : <Typography variant="body2" color="text.disabled">—</Typography>}
                    </TableCell>
                    <TableCell>{i.glAccount}</TableCell>
                    <TableCell>
                      <Chip label={i.isActive === false ? 'Inactive' : 'Active'}
                        color={i.isActive === false ? 'default' : 'success'}
                        size="small" variant="outlined" />
                    </TableCell>
                    <TableCell onClick={e => e.stopPropagation()}>
                      <IconButton color="primary" onClick={() => handleEdit(i)} sx={{ mr: 1 }}><Edit /></IconButton>
                      <IconButton color="warning" onClick={() => setDeactivateDlg(i.itemNo)}
                        disabled={i.isActive === false}><Delete /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Deactivate Confirm Dialog */}
      <Dialog open={Boolean(deactivateDlg)} onClose={() => setDeactivateDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'warning.main' }}>
          <WarningAmber color="warning" /> Deactivate Item
        </DialogTitle>
        <DialogContent>
          <Typography>Deactivate item <strong>{deactivateDlg}</strong>? It will be hidden from new transactions.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateDlg(null)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={confirmAction}>Deactivate</Button>
        </DialogActions>
      </Dialog>

      {/* ── Item Card (view) ── */}
      <Dialog open={Boolean(viewItem)} onClose={() => setViewItem(null)} maxWidth="sm" fullWidth>
        {viewItem && (
          <>
            <DialogTitle component="div">
              <Box display="flex" alignItems="center" gap={1}>
                <Inventory2 color="primary" />
                <Box>
                  <Typography variant="h6" lineHeight={1}>{viewItem.itemName}</Typography>
                  <Typography variant="caption" color="text.secondary">{viewItem.itemNo} · {viewItem.itemType}</Typography>
                </Box>
                <Box ml="auto">
                  <Chip label={viewItem.isActive === false ? 'Inactive' : 'Active'}
                    color={viewItem.isActive === false ? 'default' : 'success'}
                    size="small" variant="outlined" />
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="subtitle2" color="primary" gutterBottom>Item Details</Typography>
              <InfoRow label="Item No"     value={viewItem.itemNo} />
              <InfoRow label="Name"        value={viewItem.itemName} />
              <InfoRow label="Type"        value={viewItem.itemType} />
              <InfoRow label="Description" value={viewItem.description} />

              <Divider sx={{ my: 1.5 }} />

              <Typography variant="subtitle2" color="primary" gutterBottom>Pricing</Typography>
              <InfoRow label="Unit of Measure" value={viewItem.unitOfMeasure} />
              <InfoRow label={`Unit Price (${sym()})`} value={(viewItem.unitPrice || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })} />
              <InfoRow label="GL Account"       value={viewItem.glAccount} />

              {viewItem.itemType === 'Item' && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="subtitle2" color="primary" gutterBottom>Inventory</Typography>
                  <InfoRow label="Default Location" value={viewItem.defaultLocationCode} />
                  <InfoRow label="Reorder Point" value={viewItem.reorderPoint ? `${viewItem.reorderPoint} ${viewItem.unitOfMeasure}` : '—'} />
                  <InfoRow label="Reorder Qty"   value={viewItem.reorderQty || '—'} />
                  <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160, fontWeight: 500 }}>
                      Stock on Hand
                    </Typography>
                    <Chip
                      label={`${(stockMap[viewItem.itemNo]?.qty || 0).toFixed(2)} ${viewItem.unitOfMeasure}`}
                      color={(stockMap[viewItem.itemNo]?.qty || 0) > 0 ? 'success' : 'default'}
                      size="small" variant="outlined"
                      onClick={() => { setViewItem(null); navigate(`/payables/items/${viewItem.itemNo}/ledger`); }}
                      sx={{ cursor: 'pointer', fontWeight: 600 }}
                    />
                    <Typography variant="caption" color="text.secondary">(click for ledger)</Typography>
                  </Box>
                </>
              )}
            </DialogContent>
            <DialogActions>
              {viewItem.itemType === 'Item' && (
                <Button startIcon={<ListAlt />}
                  onClick={() => { setViewItem(null); navigate(`/payables/items/${viewItem.itemNo}/ledger`); }}>
                  Item Ledger
                </Button>
              )}
              <Button startIcon={<Edit />} onClick={() => { setViewItem(null); handleEdit(viewItem); }}>Edit</Button>
              <Button variant="outlined" onClick={() => setViewItem(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {discardDialog}
      <Dialog open={dialogOpen} onClose={attemptClose} fullWidth maxWidth="sm">
        <DialogTitle>{editingItemNo ? `Edit: ${editingItemNo}` : 'New Item / Service'}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField label="Item No" value={form.itemNo}
              onChange={e => setForm({ ...form, itemNo: e.target.value })}
              disabled={!!editingItemNo} fullWidth size="small" />
            <TextField label="Item Name" value={form.itemName}
              onChange={e => setForm({ ...form, itemName: e.target.value })}
              fullWidth size="small" />
            <TextField select label="Type" value={form.itemType}
              onChange={e => setForm({ ...form, itemType: e.target.value })}
              fullWidth size="small">
              {ITEM_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
            <TextField label="Description" value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              fullWidth size="small" multiline rows={2} />
            <TextField select label="Unit of Measure" value={form.unitOfMeasure}
              onChange={e => setForm({ ...form, unitOfMeasure: e.target.value })}
              fullWidth size="small">
              {UOM_OPTIONS.map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
            </TextField>
            <TextField label={`Unit Price (${sym()})`} value={form.unitPrice}
              onChange={e => setForm({ ...form, unitPrice: e.target.value })}
              type="number" inputProps={{ min: 0, step: '0.01' }} fullWidth size="small" />
            <TextField select label="GL Account (Expense / Asset)" value={form.glAccount}
              onChange={e => setForm({ ...form, glAccount: e.target.value })}
              fullWidth size="small">
              {glAccountOptions(glAccounts, { noneLabel: 'Select GL Account' })}
            </TextField>
            {form.itemType === 'Item' && (
              <TextField select label="Default Location (Home Store)" value={form.defaultLocationCode}
                onChange={e => setForm({ ...form, defaultLocationCode: e.target.value })}
                fullWidth size="small"
                helperText="Stock received on PO posting goes here. Never changed by transfers.">
                <MenuItem value=""><em>None</em></MenuItem>
                {locations.map(loc => (
                  <MenuItem key={loc.locationCode} value={loc.locationCode}>
                    {loc.locationCode} – {loc.locationName}
                  </MenuItem>
                ))}
              </TextField>
            )}
            {form.itemType === 'Item' && (
              <Box display="flex" gap={2}>
                <TextField label="Reorder Point" value={form.reorderPoint}
                  onChange={e => setForm({ ...form, reorderPoint: e.target.value })}
                  type="number" inputProps={{ min: 0, step: 'any' }} fullWidth size="small"
                  helperText="Flag for reorder when on-hand ≤ this (0 = off)" />
                <TextField label="Reorder Qty" value={form.reorderQty}
                  onChange={e => setForm({ ...form, reorderQty: e.target.value })}
                  type="number" inputProps={{ min: 0, step: 'any' }} fullWidth size="small"
                  helperText="Suggested order qty (0 = top up to reorder point)" />
              </Box>
            )}
            {editingItemNo && (
              <FormControlLabel
                control={<Switch checked={form.isActive}
                  onChange={e => setForm({ ...form, isActive: e.target.checked })} />}
                label="Active" />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={attemptClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ItemList;
